import { createLazyFeature } from './lazy-feature.js';
import type { SchemaInput } from './schema-input.js';
import { isUndefined } from './runtime-values.js';
const loadFflate = createLazyFeature(() => import('fflate'));

export type DecompressionPath = 'native' | 'fflate';
export type DecodeFailurePhase = 'base64' | 'inflate' | 'json';
export interface DecodeCompressedJsonDiagnostics {
  encodedLength: number;
  decoder: DecompressionPath;
  failurePhase?: DecodeFailurePhase;
}

interface DecodeCompressedJsonOptions {
  errorMessage?: string;
  maxDecompressedBytes?: number;
  reportError?: boolean;
  onDiagnostics?: (diagnostics: DecodeCompressedJsonDiagnostics) => void;
}

export const MAX_DECOMPRESSED_JSON_BYTES = 4 * 1024 * 1024;

function supportsNativeCompressionStream() {
  const NativeCompressionStream = globalThis.CompressionStream;
  if (!isUndefined(NativeCompressionStream)) {
    try { new NativeCompressionStream('deflate-raw'); return true; } catch {}
  }
  return false;
}

function supportsNativeDecompressionStream() {
  const NativeDecompressionStream = globalThis.DecompressionStream;
  if (!isUndefined(NativeDecompressionStream)) {
    try { new NativeDecompressionStream('deflate-raw'); return true; } catch {}
  }
  return false;
}

export function bytesToBase64(bytes: Uint8Array) {
  const binString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binString).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64ToBytes(base64: string) {
  const normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function writeAndReadStream(
  stream: { writable: WritableStream<BufferSource>; readable: ReadableStream<Uint8Array> },
  byteArray: Uint8Array,
) {
  const writer = stream.writable.getWriter();
  let writeError: unknown;
  // SAFETY: The adjacent allowlist, normalizer, or equality check limits this value to the stated union.
  const writeDone = writer.write(byteArray as BufferSource)
    .then(() => writer.close())
    .catch((error) => { writeError = error; });

  try {
    const buffer = await new Response(stream.readable).arrayBuffer();
    await writeDone;
    if (writeError) throw writeError;
    return buffer;
  } catch (error) {
    await writeDone;
    throw error;
  }
}

function decompressionPath(): DecompressionPath {
  return supportsNativeDecompressionStream() ? 'native' : 'fflate';
}

function joinChunks(chunks: Uint8Array[], length: number): Uint8Array {
  const output = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

async function decompressWithFflate(byteArray: Uint8Array, maxOutputBytes: number): Promise<Uint8Array> {
  const { Inflate } = await loadFflate();
  const chunks: Uint8Array[] = [];
  let outputBytes = 0;
  let failure: Error | null = null;
  const inflater = new Inflate((chunk) => {
    outputBytes += chunk.length;
    if (outputBytes > maxOutputBytes) {
      failure = new RangeError('Decompressed data exceeds the output limit');
      return;
    }
    chunks.push(chunk);
  });

  // Small source chunks keep the maximum output made by one synchronous push bounded.
  for (let offset = 0; offset < byteArray.length && !failure; offset += 64) {
    const end = Math.min(offset + 64, byteArray.length);
    inflater.push(byteArray.subarray(offset, end), end === byteArray.length);
  }
  if (failure) throw failure;
  return joinChunks(chunks, outputBytes);
}

async function decompressWithNativeStream(byteArray: Uint8Array, maxOutputBytes: number): Promise<Uint8Array> {
  const stream = new DecompressionStream('deflate-raw');
  const reader = stream.readable.getReader();
  const writer = stream.writable.getWriter();
  const writeDone = (async () => {
    for (let offset = 0; offset < byteArray.length; offset += 256) {
      // SAFETY: Uint8Array is a BufferSource accepted by the browser stream contract.
      await writer.write(byteArray.subarray(offset, offset + 256) as BufferSource);
    }
    await writer.close();
  })();
  const chunks: Uint8Array[] = [];
  let outputBytes = 0;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      outputBytes += value.length;
      if (outputBytes > maxOutputBytes) {
        await reader.cancel().catch(() => {});
        await writer.abort().catch(() => {});
        await writeDone.catch(() => {});
        throw new RangeError('Decompressed data exceeds the output limit');
      }
      chunks.push(value);
    }
    await writeDone;
    return joinChunks(chunks, outputBytes);
  } catch (error) {
    await writeDone.catch(() => {});
    throw error;
  }
}

async function decompressBytes(
  byteArray: Uint8Array,
  decoder: DecompressionPath,
  maxOutputBytes: number,
): Promise<string> {
  if (decoder === 'fflate') {
    // This is stored text, not a UTF-8 file: keep U+FEFF as data.
    return new TextDecoder('utf-8', { ignoreBOM: true }).decode(await decompressWithFflate(byteArray, maxOutputBytes));
  }

  return new TextDecoder('utf-8', { ignoreBOM: true }).decode(await decompressWithNativeStream(byteArray, maxOutputBytes));
}

export async function compressTextBytes(string: string): Promise<Uint8Array> {
  const byteArray = new TextEncoder().encode(string);
  if (!supportsNativeCompressionStream()) {
    const { deflateSync } = await loadFflate();
    return deflateSync(byteArray);
  }

  const stream = new CompressionStream('deflate-raw');
  const buffer = await writeAndReadStream(stream, byteArray);
  return new Uint8Array(buffer);
}

export async function compressText(string: string) {
  return bytesToBase64(await compressTextBytes(string));
}

export async function decompressTextBytes(bytes: Uint8Array, maxOutputBytes = MAX_DECOMPRESSED_JSON_BYTES) {
  return decompressBytes(bytes, decompressionPath(), maxOutputBytes);
}

export async function decompressText(base64: string, maxOutputBytes = MAX_DECOMPRESSED_JSON_BYTES) {
  const byteArray = base64ToBytes(base64);
  return decompressTextBytes(byteArray, maxOutputBytes);
}

export async function decodeCompressedJson(
  encoded: string,
  {
    errorMessage = 'Failed to decode state:',
    maxDecompressedBytes = MAX_DECOMPRESSED_JSON_BYTES,
    reportError = true,
    onDiagnostics,
  }: DecodeCompressedJsonOptions = {},
): Promise<SchemaInput | null> {
  const decoder = decompressionPath();
  const encodedLength = encoded.length;
  const fail = (failurePhase: DecodeFailurePhase, cause: unknown) => {
    onDiagnostics?.({ encodedLength, decoder, failurePhase });
    if (reportError) console.error(errorMessage, cause);
    return null;
  };

  let byteArray: Uint8Array;
  try {
    byteArray = base64ToBytes(encoded);
  } catch (error) {
    return fail('base64', error);
  }

  let decoded: string;
  try {
    decoded = await decompressBytes(byteArray, decoder, maxDecompressedBytes);
  } catch (error) {
    return fail('inflate', error);
  }

  try {
    // Preserve the existing tolerance for a UTF-8 signature on JSON links.
    const value = JSON.parse(decoded.startsWith('\uFEFF') ? decoded.slice(1) : decoded);
    onDiagnostics?.({ encodedLength, decoder });
    return value;
  } catch (error) {
    return fail('json', error);
  }
}

export function encodeStrokePoints(points: { lat: number; lng: number }[], scale = 1e5) {
  // Keep the existing delta scale for older readers, but retain nine decimal
  // places at the default scale. Integer subtraction avoids noisy float deltas.
  const precision = 1e4;
  const pts: number[] = [];
  let prevLat = 0;
  let prevLng = 0;
  for (const pt of points) {
    const lat = Math.round(pt.lat * (scale * precision));
    const lng = Math.round(pt.lng * (scale * precision));
    pts.push((lat - prevLat) / precision, (lng - prevLng) / precision);
    prevLat = lat;
    prevLng = lng;
  }
  return pts;
}

export function decodeStrokePoints(encoded: number[], scale = 1e5) {
  const precision = 1e4;
  const points: { lat: number; lng: number }[] = [];
  let lat = 0;
  let lng = 0;
  for (let i = 0; i < encoded.length; i += 2) {
    if (!Number.isFinite(encoded[i]) || !Number.isFinite(encoded[i + 1])) {
      throw new TypeError('Encoded stroke points must be finite lat/lng delta pairs.');
    }
    // Accumulate integer units so long paths do not collect floating-point drift.
    lat += Math.round(encoded[i] * precision);
    lng += Math.round(encoded[i + 1] * precision);
    points.push({ lat: lat / (scale * precision), lng: lng / (scale * precision) });
  }
  return points;
}
